import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import PropTypes from 'prop-types';

const mapContainerStyle = {
  width: '1100px',
  height: '650px'
};

const defaultCenter = {
  lat: -1.267444,
  lng: 36.811924,
};

const defaultZoom = 11;

const MARKER_COLORS = {
  RED: 'red',
  BLUE: 'blue',
  GREEN: 'green',
  YELLOW: 'yellow',
  PURPLE: 'purple',
  ORANGE: 'orange'
};

const getColorHex = (color) => {
  const colors = {
    'red': '#FF0000',
    'blue': '#4285F4',
    'green': '#0F9D58',
    'yellow': '#FBBC05',
    'purple': '#673AB7',
    'orange': '#FF9800'
  };
  return colors[color] || '#FF0000';
};

const FullPageMap = ({ heatmapData = [], markerData = [] }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(defaultZoom);
  const [map, setMap] = useState(null);
  const mapRef = useRef();

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMap(map);
  }, []);

  // Heatmap setup
  useEffect(() => {
    if (!map || heatmapData.length === 0) return;

    const heatmapPoints = heatmapData.map(point => ({
      location: new window.google.maps.LatLng(point.lat, point.lng),
      weight: point.weight || 1
    }));

    const heatmapLayer = new window.google.maps.visualization.HeatmapLayer({
      data: heatmapPoints,
      map,
      radius: 35,
      dissipating: true,
      opacity: 0.7
    });

    return () => heatmapLayer.setMap(null);
  }, [map, heatmapData]);

  // Markers setup
  useEffect(() => {
    if (!map || markerData.length === 0) return;

    const markers = markerData.map(marker => {
      const m = new window.google.maps.Marker({
        position: { lat: marker.lat, lng: marker.lng },
        map,
        title: marker.address || '',
        icon: {
          path: window.google.maps.SymbolPath.BACKWARD_OPEN_ARROW,
          scale: 8,
          fillColor: getColorHex(marker.color || 'red'),
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#FFFFFF'
        }
      });

      m.addListener('click', () => {
        setSelectedLocation(marker);
        setCenter({ lat: marker.lat, lng: marker.lng });
        setZoom(15);
      });

      return m;
    });

    return () => {
      markers.forEach(m => m.setMap(null));
    };
  }, [map, markerData]);

  // Auto-zoom to fit all points
  useEffect(() => {
    if (!map) return;

    const bounds = new window.google.maps.LatLngBounds();

    heatmapData.forEach(point => bounds.extend(new window.google.maps.LatLng(point.lat, point.lng)));
    markerData.forEach(marker => bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng)));

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 50 });
    }
  }, [map, heatmapData, markerData]);

  return (
    <div style={{ position: 'relative' }}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={zoom}
        onLoad={onMapLoad}
        options={{
          disableDefaultUI: true,
          gestureHandling: 'greedy'
        }}
      />

      {selectedLocation && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          backgroundColor: 'white',
          padding: '10px 20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}>
          <p>Selected Location: {selectedLocation.address || 'Clicked location'}</p>
          <p>Latitude: {selectedLocation.lat.toFixed(6)}</p>
          <p>Longitude: {selectedLocation.lng.toFixed(6)}</p>
        </div>
      )}
    </div>
  );
};

FullPageMap.propTypes = {
  heatmapData: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      weight: PropTypes.number,
    })
  ),
  markerData: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      color: PropTypes.oneOf(Object.values(MARKER_COLORS)),
      address: PropTypes.string,
    })
  ),
};

export { FullPageMap, MARKER_COLORS };
